import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, apiPost, apiPatch, type AuthUser } from '../lib/auth';
import { Toast, type ToastVariant } from '../components/Toast';
import { useLang } from '../lib/lang';

const BN_TPA = { back: 'Laman Utama', title: 'Penilaian Prestasi Guru', subtitle: 'Teacher Performance Appraisal', emptyTitle: 'Tiada Penilaian', emptyBody: 'Tiada rekod TPA dijumpai.', retry: 'Cuba Lagi', midYear: 'Pertengahan Tahun', endYear: 'Akhir Tahun', overall: 'Keseluruhan', submit: 'Hantar', acknowledge: 'Akui', finalise: 'Muktamadkan', close: '✕ Tutup' };
const EN_TPA = { back: 'Home', title: 'Teacher Performance Appraisal', subtitle: 'Educator Evaluation', emptyTitle: 'No Appraisals', emptyBody: 'No TPA records found.', retry: 'Retry', midYear: 'Mid Year', endYear: 'End of Year', overall: 'Overall', submit: 'Submit', acknowledge: 'Acknowledge', finalise: 'Finalise', close: '✕ Close' };

interface Props { user: AuthUser }

interface TpaEval {
  id: string; cycle: string; school_year_id: string; school_year_name: string | null;
  lo1_score: string | null; lo2_score: string | null; overall_score: string | null;
  overall_grade: string | null;
  lo1_breakdown: Record<string, unknown>; lo2_breakdown: Record<string, unknown>;
  evaluator_remarks_ms: string | null; evaluator_remarks_en: string | null;
  areas_of_strength: string | null; development_plan: string | null;
  status: string; submitted_at: string | null; acknowledged_at: string | null;
  finalised_at: string | null; educator_name: string | null; evaluator_name: string | null;
}

const CYCLE_LABELS: Record<string, string> = { MID_YEAR: 'Pertengahan Tahun', END_YEAR: 'Akhir Tahun' };

const STATUS_CFG: Record<string, { label: string; color: string; next?: string; nextLabel?: string }> = {
  DRAFT:        { label: 'Draf',        color: '#9ca3af', next: 'SUBMITTED',    nextLabel: 'Hantar / Submit' },
  SUBMITTED:    { label: 'Dihantar',    color: '#60a5fa', next: 'ACKNOWLEDGED', nextLabel: 'Akui / Acknowledge' },
  ACKNOWLEDGED: { label: 'Diakui',      color: '#facc15', next: 'FINALISED',    nextLabel: 'Muktamadkan / Finalise' },
  FINALISED:    { label: 'Dimuktamad', color: '#4ade80' },
};

function gradeColor(g: string | null): string {
  if (!g) return '#9ca3af';
  if (g.startsWith('A')) return '#4ade80';
  if (g.startsWith('B')) return '#86efac';
  if (g === 'C') return '#facc15';
  if (g === 'D') return '#fb923c';
  return '#f87171';
}

// LO criteria labels for the breakdown UI
const LO1_CRITERIA = [
  { key: 'pedagogy',    label: 'Pedagogi / Pedagogy' },
  { key: 'content',     label: 'Pengetahuan Kandungan / Content Knowledge' },
  { key: 'classroom',   label: 'Pengurusan Kelas / Classroom Management' },
  { key: 'assessment',  label: 'Pentaksiran / Assessment' },
  { key: 'profdev',     label: 'Pembangunan Profesional / Prof. Development' },
];
const LO2_CRITERIA = [
  { key: 'collaboration', label: 'Kerjasama / Collaboration' },
  { key: 'contribution',  label: 'Sumbangan / School Contribution' },
  { key: 'leadership',    label: 'Kepimpinan / Leadership' },
];

// Score → Grade preview
function computeGrade(lo1: number, lo2: number): { overall: number; grade: string } {
  const overall = Math.round((lo1 * 0.70 + lo2 * 0.30) * 100) / 100;
  let grade = 'E';
  if (overall >= 90) grade = 'A+';
  else if (overall >= 80) grade = 'A';
  else if (overall >= 70) grade = 'B+';
  else if (overall >= 60) grade = 'B';
  else if (overall >= 50) grade = 'C';
  else if (overall >= 40) grade = 'D';
  return { overall, grade };
}

export function TPAPage({ user }: Props) {
  const { lang } = useLang();
  const UI = lang === 'en' ? EN_TPA : BN_TPA;
  const [searchParams] = useSearchParams();
  const educatorId = searchParams.get('educatorId') ?? '';

  const [evals,    setEvals]    = useState<TpaEval[]>([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [toast,    setToast]    = useState<{ message: string; variant: ToastVariant } | null>(null);

  const [showForm,   setShowForm]   = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [transitioning, setTransitioning] = useState<string | null>(null);

  // Form state
  const [form, setForm] = useState({
    schoolYearId: '00000000-0000-0000-0000-000000000001', // demo placeholder
    cycle: 'MID_YEAR' as 'MID_YEAR' | 'END_YEAR',
    lo1Score: '',
    lo2Score: '',
    lo1Breakdown: {} as Record<string, string>,
    lo2Breakdown: {} as Record<string, string>,
    evaluatorRemarksMs: '',
    evaluatorRemarksEn: '',
    areasOfStrength: '',
    developmentPlan: '',
  });
  const [formError, setFormError] = useState<string | null>(null);

  const isAdmin = user.roles.some((r) => ['admin', 'principal', 'super_admin'].includes(r.role));

  const load = useCallback(async () => {
    if (!educatorId) { setError('URL mesti menyertakan ?educatorId=UUID. / URL must include ?educatorId=UUID.'); return; }
    setLoading(true); setError(null);
    try {
      setEvals(await apiGet<TpaEval[]>(`/api/tpa/educators/${educatorId}/evaluations`));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ralat memuatkan penilaian.');
    } finally { setLoading(false); }
  }, [educatorId]);

  useEffect(() => { void load(); }, [load]);

  // Live grade preview
  const lo1Num = parseFloat(form.lo1Score) || 0;
  const lo2Num = parseFloat(form.lo2Score) || 0;
  const preview = (form.lo1Score || form.lo2Score) ? computeGrade(lo1Num, lo2Num) : null;

  function validateForm(): string | null {
    if (!form.schoolYearId.trim()) return 'Sila masukkan ID tahun persekolahan. / Enter school year ID.';
    const lo1 = parseFloat(form.lo1Score);
    const lo2 = parseFloat(form.lo2Score);
    if (form.lo1Score && (isNaN(lo1) || lo1 < 0 || lo1 > 100)) return 'Skor LO1 mesti antara 0–100. / LO1 score must be 0–100.';
    if (form.lo2Score && (isNaN(lo2) || lo2 < 0 || lo2 > 100)) return 'Skor LO2 mesti antara 0–100. / LO2 score must be 0–100.';
    return null;
  }

  async function handleSave() {
    const err = validateForm();
    if (err) { setFormError(err); return; }
    if (!educatorId) { setFormError('Tiada educatorId dalam URL.'); return; }

    setSaving(true); setFormError(null);
    try {
      await apiPost(`/api/tpa/educators/${educatorId}/evaluations`, {
        schoolYearId:       form.schoolYearId,
        cycle:              form.cycle,
        lo1Score:           form.lo1Score ? parseFloat(form.lo1Score) : undefined,
        lo2Score:           form.lo2Score ? parseFloat(form.lo2Score) : undefined,
        lo1Breakdown:       form.lo1Breakdown,
        lo2Breakdown:       form.lo2Breakdown,
        evaluatorRemarksMs: form.evaluatorRemarksMs.trim() || undefined,
        evaluatorRemarksEn: form.evaluatorRemarksEn.trim() || undefined,
        areasOfStrength:    form.areasOfStrength.trim() || undefined,
        developmentPlan:    form.developmentPlan.trim() || undefined,
      });
      setToast({ variant: 'success', message: 'Penilaian disimpan. / Evaluation saved.' });
      setShowForm(false);
      void load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Gagal menyimpan. / Failed to save.');
    } finally { setSaving(false); }
  }

  async function handleTransition(evalId: string, newStatus: string) {
    setTransitioning(evalId);
    try {
      await apiPatch(`/api/tpa/evaluations/${evalId}/status`, { status: newStatus });
      setToast({ variant: 'success', message: `Status dikemaskini kepada ${newStatus}. / Status updated to ${newStatus}.` });
      void load();
    } catch (e) {
      setToast({ variant: 'error', message: e instanceof Error ? e.message : 'Gagal menukar status. / Failed to change status.' });
    } finally { setTransitioning(null); }
  }

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>

          {/* Nav */}
          <div style={{ marginBottom: spacing.xl }}>
            <Link to="/" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>← {UI.back}</Link>
            {educatorId && (
              <Link to={`/admin/educators/${educatorId}`} style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none', marginLeft: spacing.md }}>
                ← Profil Pendidik
              </Link>
            )}
          </div>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing.xl, gap: spacing.md, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
                📋 Penilaian TPA
              </h1>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                Teacher Performance Appraisal — LO1 (70%) + LO2 (30%)
              </p>
            </div>
            {isAdmin && !showForm && (
              <Button variant="primary" size="sm" onClick={() => { setShowForm(true); setFormError(null); }}>
                + Penilaian Baharu
              </Button>
            )}
          </div>

          {/* ── Upsert form ─────────────────────────────────────────────── */}
          {showForm && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, marginBottom: spacing.lg, boxShadow: shadows.xl }}>
              <p style={{ margin: `0 0 ${spacing.lg}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
                Borang Penilaian TPA / TPA Evaluation Form
              </p>

              <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.md }}>
                <div style={{ flex: '1 1 160px' }}>
                  <label style={labelSt}>Kitaran / Cycle *</label>
                  <select value={form.cycle} onChange={(e) => setForm((f) => ({ ...f, cycle: e.target.value as 'MID_YEAR' | 'END_YEAR' }))} disabled={saving} style={inputSt()}>
                    <option value="MID_YEAR">{UI.midYear}</option>
                    <option value="END_YEAR">{UI.endYear}</option>
                  </select>
                </div>
                <div style={{ flex: '2 1 220px' }}>
                  <label style={labelSt}>ID Tahun Persekolahan</label>
                  <input type="text" value={form.schoolYearId}
                    onChange={(e) => setForm((f) => ({ ...f, schoolYearId: e.target.value }))}
                    placeholder="UUID tahun persekolahan"
                    disabled={saving} style={inputSt()} />
                </div>
              </div>

              {/* LO scores + live preview */}
              <div style={{ display: 'flex', gap: spacing.md, flexWrap: 'wrap', marginBottom: spacing.md, alignItems: 'flex-start' }}>
                <div style={{ flex: '1 1 140px' }}>
                  <label style={labelSt}>Skor LO1 (0–100) — 70%</label>
                  <input
                    type="number" min={0} max={100} step={0.01}
                    value={form.lo1Score}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || (parseFloat(v) >= 0 && parseFloat(v) <= 100)) {
                        setForm((f) => ({ ...f, lo1Score: v }));
                      }
                    }}
                    placeholder="0–100" disabled={saving} style={inputSt()} />
                </div>
                <div style={{ flex: '1 1 140px' }}>
                  <label style={labelSt}>Skor LO2 (0–100) — 30%</label>
                  <input
                    type="number" min={0} max={100} step={0.01}
                    value={form.lo2Score}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || (parseFloat(v) >= 0 && parseFloat(v) <= 100)) {
                        setForm((f) => ({ ...f, lo2Score: v }));
                      }
                    }}
                    placeholder="0–100" disabled={saving} style={inputSt()} />
                </div>

                {/* Live grade preview */}
                {preview && (
                  <div style={{
                    flex: '1 1 120px', backgroundColor: `${gradeColor(preview.grade)}18`,
                    border: `1px solid ${gradeColor(preview.grade)}44`,
                    borderRadius: borderRadius.xl, padding: spacing.md, textAlign: 'center',
                  }}>
                    <p style={{ margin: 0, fontSize: '28px', fontWeight: typography.fontWeight.bold, color: gradeColor(preview.grade) }}>
                      {preview.grade}
                    </p>
                    <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                      {preview.overall.toFixed(2)} / 100
                    </p>
                    <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                      Pratonton / Preview
                    </p>
                  </div>
                )}
              </div>

              {/* LO1 Breakdown */}
              <details style={{ marginBottom: spacing.md }}>
                <summary style={{ cursor: 'pointer', fontSize: typography.fontSize.sm, color: colors.neutral.gray400, marginBottom: spacing.sm }}>
                  ▶ Pecahan LO1 (pilihan) / LO1 Breakdown (optional)
                </summary>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', paddingTop: spacing.sm }}>
                  {LO1_CRITERIA.map((c) => (
                    <div key={c.key} style={{ flex: '1 1 150px' }}>
                      <label style={labelSt}>{c.label}</label>
                      <input type="number" min={0} max={100} step={0.01}
                        value={form.lo1Breakdown[c.key] ?? ''}
                        onChange={(e) => setForm((f) => ({ ...f, lo1Breakdown: { ...f.lo1Breakdown, [c.key]: e.target.value } }))}
                        placeholder="0–100" disabled={saving} style={inputSt()} />
                    </div>
                  ))}
                </div>
              </details>

              {/* LO2 Breakdown */}
              <details style={{ marginBottom: spacing.md }}>
                <summary style={{ cursor: 'pointer', fontSize: typography.fontSize.sm, color: colors.neutral.gray400, marginBottom: spacing.sm }}>
                  ▶ Pecahan LO2 (pilihan) / LO2 Breakdown (optional)
                </summary>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', paddingTop: spacing.sm }}>
                  {LO2_CRITERIA.map((c) => (
                    <div key={c.key} style={{ flex: '1 1 150px' }}>
                      <label style={labelSt}>{c.label}</label>
                      <input type="number" min={0} max={100} step={0.01}
                        value={form.lo2Breakdown[c.key] ?? ''}
                        onChange={(e) => setForm((f) => ({ ...f, lo2Breakdown: { ...f.lo2Breakdown, [c.key]: e.target.value } }))}
                        placeholder="0–100" disabled={saving} style={inputSt()} />
                    </div>
                  ))}
                </div>
              </details>

              {/* Remarks + development plan */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm, marginBottom: spacing.md }}>
                <div>
                  <label style={labelSt}>Ulasan Penilai (BM) / Evaluator Remarks (MS)</label>
                  <textarea
                    value={form.evaluatorRemarksMs}
                    onChange={(e) => setForm((f) => ({ ...f, evaluatorRemarksMs: e.target.value }))}
                    rows={3} maxLength={3000} disabled={saving}
                    style={{ ...inputSt(), resize: 'vertical', lineHeight: '1.5' }}
                    placeholder="Ulasan dalam Bahasa Melayu…"
                  />
                </div>
                <div>
                  <label style={labelSt}>Evaluator Remarks (EN)</label>
                  <textarea
                    value={form.evaluatorRemarksEn}
                    onChange={(e) => setForm((f) => ({ ...f, evaluatorRemarksEn: e.target.value }))}
                    rows={3} maxLength={3000} disabled={saving}
                    style={{ ...inputSt(), resize: 'vertical', lineHeight: '1.5' }}
                    placeholder="Remarks in English…"
                  />
                </div>
                <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelSt}>Bidang Kekuatan / Areas of Strength</label>
                    <textarea
                      value={form.areasOfStrength}
                      onChange={(e) => setForm((f) => ({ ...f, areasOfStrength: e.target.value }))}
                      rows={2} maxLength={2000} disabled={saving}
                      style={{ ...inputSt(), resize: 'vertical' }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelSt}>Pelan Pembangunan / Development Plan</label>
                    <textarea
                      value={form.developmentPlan}
                      onChange={(e) => setForm((f) => ({ ...f, developmentPlan: e.target.value }))}
                      rows={2} maxLength={2000} disabled={saving}
                      style={{ ...inputSt(), resize: 'vertical' }}
                    />
                  </div>
                </div>
              </div>

              {formError && <p style={{ margin: `0 0 ${spacing.sm}`, color: '#f87171', fontSize: typography.fontSize.sm }}>{formError}</p>}

              <div style={{ display: 'flex', gap: spacing.sm }}>
                <Button variant="primary" size="md" onClick={() => void handleSave()} disabled={saving} loading={saving}>
                  {saving ? 'Menyimpan…' : 'Simpan Draf / Save Draft'}
                </Button>
                <Button variant="ghost" size="md" onClick={() => { setShowForm(false); setFormError(null); }} disabled={saving}>
                  Batal
                </Button>
              </div>
            </div>
          )}

          {/* ── Evaluations list ─────────────────────────────────────────── */}
          {error && (
            <div style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.md }}>
              <p style={{ margin: 0, color: '#f87171', fontSize: typography.fontSize.sm }}>{error}</p>
              <Button variant="ghost" size="sm" onClick={() => void load()} style={{ marginTop: spacing.xs }}>Cuba Lagi</Button>
            </div>
          )}
          {loading && <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}><Spinner size="lg" /></div>}

          {!loading && !error && evals.length === 0 && !showForm && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, textAlign: 'center' }}>
              <div style={{ fontSize: '40px', marginBottom: spacing.sm }}>📋</div>
              <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{UI.emptyTitle}</p>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                No TPA evaluations recorded. {isAdmin ? 'Use the button above to create one.' : ''}
              </p>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
            {evals.map((ev) => {
              const sc      = STATUS_CFG[ev.status] ?? { label: ev.status, color: '#9ca3af' };
              const overall = ev.overall_score ? parseFloat(ev.overall_score) : null;
              const isTrans = transitioning === ev.id;

              return (
                <div key={ev.id} style={{
                  backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl,
                  border: `1px solid ${ev.status === 'FINALISED' ? 'rgba(74,222,128,0.3)' : colors.neutral.gray800}`,
                  overflow: 'hidden', boxShadow: shadows.lg,
                }}>
                  {/* Card header */}
                  <div style={{ padding: spacing.lg, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: spacing.md, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: spacing.xs, marginBottom: spacing.xs, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ padding: '2px 10px', backgroundColor: `${sc.color}22`, color: sc.color, borderRadius: borderRadius.full, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold }}>
                          {sc.label}
                        </span>
                        <span style={{ padding: '2px 10px', backgroundColor: `rgba(255,255,255,0.06)`, color: colors.neutral.gray400, borderRadius: borderRadius.full, fontSize: typography.fontSize.xs }}>
                          {ev.cycle === 'MID_YEAR' ? UI.midYear : ev.cycle === 'END_YEAR' ? UI.endYear : ev.cycle}
                        </span>
                        {ev.school_year_name && (
                          <span style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>{ev.school_year_name}</span>
                        )}
                      </div>
                      <p style={{ margin: 0, fontSize: typography.fontSize.sm, color: colors.neutral.gray400 }}>
                        Penilai: {ev.evaluator_name ?? 'Tidak Diketahui'}
                      </p>
                    </div>

                    {/* Grade badge */}
                    {ev.overall_grade && (
                      <div style={{
                        textAlign: 'center', minWidth: '72px',
                        backgroundColor: `${gradeColor(ev.overall_grade)}18`,
                        borderRadius: borderRadius.xl, padding: `${spacing.xs} ${spacing.sm}`,
                        border: `1px solid ${gradeColor(ev.overall_grade)}33`,
                      }}>
                        <p style={{ margin: 0, fontSize: '28px', fontWeight: typography.fontWeight.bold, color: gradeColor(ev.overall_grade), lineHeight: 1 }}>
                          {ev.overall_grade}
                        </p>
                        <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                          {overall?.toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Score breakdown bar */}
                  {(ev.lo1_score || ev.lo2_score) && (
                    <div style={{ padding: `0 ${spacing.lg} ${spacing.md}`, display: 'flex', gap: spacing.md, flexWrap: 'wrap' }}>
                      {ev.lo1_score && (
                        <div style={{ flex: '7 0 0' }}>
                          <p style={{ margin: `0 0 4px`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                            LO1 — {parseFloat(ev.lo1_score).toFixed(1)} / 100 (70%)
                          </p>
                          <div style={{ height: '6px', backgroundColor: colors.neutral.gray800, borderRadius: borderRadius.full, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${parseFloat(ev.lo1_score)}%`, backgroundColor: '#60a5fa', borderRadius: borderRadius.full }} />
                          </div>
                        </div>
                      )}
                      {ev.lo2_score && (
                        <div style={{ flex: '3 0 0' }}>
                          <p style={{ margin: `0 0 4px`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                            LO2 — {parseFloat(ev.lo2_score).toFixed(1)} / 100 (30%)
                          </p>
                          <div style={{ height: '6px', backgroundColor: colors.neutral.gray800, borderRadius: borderRadius.full, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${parseFloat(ev.lo2_score)}%`, backgroundColor: '#a78bfa', borderRadius: borderRadius.full }} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Remarks */}
                  {(ev.evaluator_remarks_ms || ev.areas_of_strength || ev.development_plan) && (
                    <div style={{ padding: `0 ${spacing.lg} ${spacing.md}`, display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                      {ev.evaluator_remarks_ms && (
                        <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400, lineHeight: '1.5' }}>
                          <strong style={{ color: colors.neutral.gray200 }}>Ulasan: </strong>{ev.evaluator_remarks_ms}
                        </p>
                      )}
                      {ev.areas_of_strength && (
                        <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: '#4ade80', lineHeight: '1.5' }}>
                          <strong>Kekuatan: </strong>{ev.areas_of_strength}
                        </p>
                      )}
                      {ev.development_plan && (
                        <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: '#facc15', lineHeight: '1.5' }}>
                          <strong>Pelan Pembangunan: </strong>{ev.development_plan}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Status action */}
                  {sc.next && (
                    <div style={{ padding: `${spacing.sm} ${spacing.lg}`, borderTop: `1px solid ${colors.neutral.gray800}`, backgroundColor: 'rgba(255,255,255,0.01)', display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Button
                        variant="primary" size="sm"
                        onClick={() => void handleTransition(ev.id, sc.next!)}
                        disabled={!!transitioning}
                        loading={isTrans}
                      >
                        {isTrans ? 'Memproses…' : sc.nextLabel!}
                      </Button>
                      {ev.submitted_at && (
                        <span style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                          {lang === 'en' ? 'Submitted:' : 'Dihantar:'} {new Date(ev.submitted_at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'ms-BN')}
                        </span>
                      )}
                      {ev.acknowledged_at && (
                        <span style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                          {lang === 'en' ? 'Acknowledged:' : 'Diakui:'} {new Date(ev.acknowledged_at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'ms-BN')}
                        </span>
                      )}
                    </div>
                  )}
                  {ev.status === 'FINALISED' && ev.finalised_at && (
                    <div style={{ padding: `${spacing.xs} ${spacing.lg}`, borderTop: `1px solid ${colors.neutral.gray800}` }}>
                      <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: '#4ade80' }}>
                        {lang === 'en' ? '✓ Finalised on' : '✓ Dimuktamadkan pada'} {new Date(ev.finalised_at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'ms-BN')}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}

const labelSt: React.CSSProperties = { display: 'block', fontSize: typography.fontSize.xs, color: colors.neutral.gray400, marginBottom: spacing.xs };
function inputSt(): React.CSSProperties {
  return {
    width: '100%', backgroundColor: colors.neutral.black,
    border: `1px solid ${colors.neutral.gray800}`, borderRadius: borderRadius.md,
    color: colors.neutral.white, padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typography.fontSize.sm, outline: 'none',
    boxSizing: 'border-box', fontFamily: typography.fontFamily.body,
  };
}
